import { weekStringFromDate } from './iso-week'
import { isFeestdag, type PublicHolidayInfo } from './leave-calculations'

export type WorkedDayType = 'WERKDAG' | 'ZATERDAG' | 'ZONDAG' | 'FEESTDAG'

export interface SplittableEntry {
  /** Startdag van de dienst (UTC-dag of ISO `yyyy-MM-dd`). */
  date: Date | string
  /** Geplande starttijd `HH:mm`. */
  plannedStart: string
  /** Geplande eindtijd `HH:mm`. Mag < start zijn (dienst loopt over middernacht). */
  plannedEnd: string
  /** Werkelijke starttijd `HH:mm` — wint van planned als beide actuals gezet zijn. */
  actualStart?: string | null
  /** Werkelijke eindtijd `HH:mm`. */
  actualEnd?: string | null
  /** Pauze in minuten — niet meer gebruikt (`hours` is al netto); behouden voor compat. */
  pauzeMinutes?: number
  /**
   * Vooraf berekende (autoritatieve) netto uren van de dayEntry. Dit totaal wordt
   * over de dag-delen verdeeld — de tijden bepalen alléén de verdeling — zodat de
   * som van de buckets exact gelijk blijft aan de opgeslagen uren (geen recompute-
   * divergentie). Verplicht.
   */
  hours: number
}

export interface WorkedHoursBucket {
  /** Kalenderdag waarop dit deel valt (ISO `yyyy-MM-dd`). */
  datum: string
  /** ISO-week `wk-jaar` (bijv. `20-2026`). */
  isoWeek: string
  dayType: WorkedDayType
  /** Netto gewerkte uren in dit deel (2 decimalen; som over de buckets == netto). */
  uren: number
}

const MIN_PER_DAY = 24 * 60

function toUtcDay(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(`${date}T00:00:00.000Z`) : date
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/** Parse `HH:mm` naar minuten; `null` bij ongeldige/lege input (geen throw). */
function safeMinutes(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function dayTypeFor(day: Date, feestdagen?: PublicHolidayInfo[]): WorkedDayType {
  // `day` is een UTC-middernacht-datum → vergelijk feestdagen óók op UTC-dag
  // (useUtc=true). Anders zou een feestdag-nachtdienst door TZ-drift (server in
  // UTC vs lokale opslag) naast de feestdag kunnen vallen. Verlof gebruikt de
  // lokale-dag-variant van isFeestdag; die blijft ongemoeid.
  if (feestdagen && feestdagen.length > 0 && isFeestdag(day, feestdagen, true)) return 'FEESTDAG'
  const dow = day.getUTCDay() // 0 = zondag .. 6 = zaterdag
  if (dow === 0) return 'ZONDAG'
  if (dow === 6) return 'ZATERDAG'
  return 'WERKDAG'
}

function bucketFor(day: Date, uren: number, feestdagen?: PublicHolidayInfo[]): WorkedHoursBucket {
  return {
    datum: day.toISOString().slice(0, 10),
    isoWeek: weekStringFromDate(day),
    dayType: dayTypeFor(day, feestdagen),
    uren,
  }
}

/**
 * Splitst één dayEntry (zoals de monteur die invult: één start + eindtijd) in
 * netto uren-buckets per kalenderdag/ISO-week.
 *
 * - Effectieve tijden = actuals indien beide gezet, anders planned (gelijk aan
 *   `computeDayEntryHours`).
 * - Een dienst die over middernacht loopt (eind < start) wordt op de middernacht-
 *   grens gesplitst zodat elk deel in zijn eigen dag + ISO-week valt — bv.
 *   zondag 22:00–06:00 → ~2u op zondag (week N) + ~6u op maandag (week N+1).
 * - De netto uren (`hours`) worden evenredig op de bruto-duur over de delen
 *   verdeeld; de **som van de bucket-uren is exact gelijk aan `hours`** (het
 *   laatste deel sluit af), zodat weektotalen/verloning/factuur nooit uren
 *   winnen of verliezen.
 * - Robuust: zijn de tijden onbruikbaar (slechte data), dan géén split maar één
 *   bucket op de startdag met `entry.hours` als fallback — nooit een throw.
 *
 * Gedeelde bron-helper voor weektotalen, verloningsperiode, capaciteit, toeslag-
 * berekening en factuurregels (zie `docs/specs/23-nachtdienst-urensplit-configureerbare-toeslagen.md`).
 */
export function splitDayEntryByDay(
  entry: SplittableEntry,
  opts?: { feestdagen?: PublicHolidayInfo[] },
): WorkedHoursBucket[] {
  const useActual = entry.actualStart != null && entry.actualEnd != null
  const startStr = useActual ? entry.actualStart! : entry.plannedStart
  const endStr = useActual ? entry.actualEnd! : entry.plannedEnd

  const startDay = toUtcDay(entry.date)
  const startMin = safeMinutes(startStr)
  let endMin = safeMinutes(endStr)

  // Onbruikbare tijden → geen split; fallback naar de vooraf berekende uren.
  if (startMin == null || endMin == null) {
    const fallback = round2(entry.hours ?? 0)
    return fallback > 0 ? [bucketFor(startDay, fallback, opts?.feestdagen)] : []
  }

  if (endMin < startMin) endMin += MIN_PER_DAY // dienst loopt over middernacht
  const grossTotal = endMin - startMin
  // Netto totaal = de autoritatieve opgeslagen uren; de tijden bepalen alleen de
  // dag/week-verdeling, zodat de som nooit afwijkt van wat er geboekt staat.
  const netTotal = round2(entry.hours ?? 0)
  if (grossTotal <= 0 || netTotal <= 0) {
    return netTotal > 0 ? [bucketFor(startDay, netTotal, opts?.feestdagen)] : []
  }

  // Bruto dag-segmenten (gesplitst op elke middernacht-grens).
  const segments: { dayIndex: number; segGross: number }[] = []
  let cursor = startMin
  while (cursor < endMin) {
    const dayIndex = Math.floor(cursor / MIN_PER_DAY)
    const dayBoundary = (dayIndex + 1) * MIN_PER_DAY
    const segEnd = Math.min(endMin, dayBoundary)
    segments.push({ dayIndex, segGross: segEnd - cursor })
    cursor = segEnd
  }

  // Netto uren evenredig op de bruto-duur verdelen; laatste deel sluit exact af
  // zodat de som === netTotal blijft (geen afrond-lek).
  const buckets: WorkedHoursBucket[] = []
  let allocated = 0
  segments.forEach((seg, i) => {
    const uren =
      i < segments.length - 1
        ? round2((netTotal * seg.segGross) / grossTotal)
        : round2(netTotal - allocated)
    allocated += uren
    const day = new Date(
      Date.UTC(startDay.getUTCFullYear(), startDay.getUTCMonth(), startDay.getUTCDate() + seg.dayIndex),
    )
    buckets.push(bucketFor(day, uren, opts?.feestdagen))
  })
  return buckets
}

// ────────────────────────────────────────────────────────────────────────────
// Toerekening: bij welke dag/week/maand horen de UREN van een dienst
// ────────────────────────────────────────────────────────────────────────────
//
// PO-besluit 2026-10-16: een NACHTDIENST hoort in zijn geheel bij de dag waarop
// hij EINDIGT. Zondag 22:00-06:00 is een maandagdienst: alle uren tellen in de
// week (en maand) van die maandag, niet 2u in week N en 6u in week N+1.
//
//   - Nachtdienst = loopt over middernacht én het grootste deel (of de helft)
//     van de bruto-duur valt ná middernacht. Een late dienst 14:45-00:45 blijft
//     dus bij de startdag, met de bestaande verdeling over beide kalenderdagen.
//   - Geldt alleen voor diensten die starten op of na `NACHTDIENST_EINDDAG_VANAF`.
//     Eerdere diensten zitten al in afgesloten weken/verloonde maanden en
//     houden hun oude verdeling, zodat er op de overgang niets dubbel of weg valt.
//   - ALLEEN de uren-toerekening (weekstaat, overwerk, ATV/TVT, dag-staffel,
//     maand). De TOESLAG blijft per klokuur via `splitDayEntryByDay`: 22:00-24:00
//     op zondag is nog steeds een zondaguur voor de toeslag-engine.

/** Eerste startdag (ISO) waarop een nachtdienst naar zijn einddag gaat. */
export const NACHTDIENST_EINDDAG_VANAF = '2026-10-12'

/**
 * De einddag (ISO `yyyy-MM-dd`) als deze dienst een nachtdienst is die naar
 * zijn einddag wordt toegerekend; anders `null`.
 */
function nachtdienstEinddag(entry: SplittableEntry): string | null {
  const startDay = toUtcDay(entry.date)
  const startKey = startDay.toISOString().slice(0, 10)
  if (startKey < NACHTDIENST_EINDDAG_VANAF) return null

  const useActual = entry.actualStart != null && entry.actualEnd != null
  const startMin = safeMinutes(useActual ? entry.actualStart! : entry.plannedStart)
  const endMin = safeMinutes(useActual ? entry.actualEnd! : entry.plannedEnd)
  if (startMin == null || endMin == null) return null
  // Alleen een dienst die over middernacht loopt (eind vóór start). Precies tot
  // 00:00 eindigen (eind == 0) telt niet: er valt niets ná middernacht.
  if (endMin >= startMin) return null

  const voorMiddernacht = MIN_PER_DAY - startMin
  const naMiddernacht = endMin
  if (naMiddernacht < voorMiddernacht) return null

  const eind = new Date(
    Date.UTC(startDay.getUTCFullYear(), startDay.getUTCMonth(), startDay.getUTCDate() + 1),
  )
  return eind.toISOString().slice(0, 10)
}

/**
 * De dag (ISO `yyyy-MM-dd`) waaraan een dienst als geheel wordt toegerekend:
 * de einddag voor een nachtdienst (zie boven), anders de startdag. Bepaalt in
 * welke week en maand een dagregel valt (weekstaat-selectie, payroll-maand).
 */
export function toerekeningsDag(entry: SplittableEntry): string {
  return nachtdienstEinddag(entry) ?? toUtcDay(entry.date).toISOString().slice(0, 10)
}

/**
 * Uren-buckets per kalenderdag voor de UREN-toerekening (weekstaat, overwerk,
 * dag-staffel). Een nachtdienst levert één bucket op zijn einddag met alle uren;
 * elke andere dienst de klokuur-verdeling van `splitDayEntryByDay`.
 *
 * NIET gebruiken voor de toeslag: die blijft per klokuur (`splitDayEntryByDay`).
 */
export function verdeelUrenOverDagen(
  entry: SplittableEntry,
  opts?: { feestdagen?: PublicHolidayInfo[] },
): WorkedHoursBucket[] {
  const einddag = nachtdienstEinddag(entry)
  if (einddag == null) return splitDayEntryByDay(entry, opts)
  const uren = round2(entry.hours ?? 0)
  if (uren <= 0) return []
  return [bucketFor(new Date(`${einddag}T00:00:00.000Z`), uren, opts?.feestdagen)]
}
